import type { EquipmentId, ClientId } from "../../../shared/kernel/identifiers.js";
import type {
  Equipment,
  EquipmentRepository,
  EquipmentSnapshot,
  EquipmentDocument
} from "../../../equipment/equipment-domain.js";

export class InMemoryEquipmentRepository implements EquipmentRepository {
  private equipments = new Map<string, Equipment>();
  private snapshots = new Map<string, EquipmentSnapshot>();
  private documents = new Map<string, EquipmentDocument>();

  /**
   * Lote (Bloco 04): cada `saveEquipment`/`deleteEquipment` normalmente
   * regrava as TRÊS tabelas inteiras no localStorage a cada chamada — correto
   * para uma edição isolada, mas O(n²) numa importação de milhares de linhas
   * (cada gravação intermediária já regrava um conjunto que só cresce).
   * `beginBatch`/`endBatch` suspendem essa gravação intermediária e fazem
   * uma única gravação real ao final do lote.
   */
  private batchDepth = 0;

  constructor() {
    try {
      if (typeof localStorage !== 'undefined') {
        const eqData = localStorage.getItem('inspect_equipments');
        if (eqData) this.equipments = new Map(JSON.parse(eqData));
        const snapData = localStorage.getItem('inspect_snapshots');
        if (snapData) this.snapshots = new Map(JSON.parse(snapData));
        const docData = localStorage.getItem('inspect_equipment_documents');
        if (docData) this.documents = new Map(JSON.parse(docData));
      }
    } catch (e) {
      console.warn("Failed to load equipment from localStorage", e);
    }
  }

  beginBatch(): void {
    this.batchDepth++;
  }

  /**
   * Fecha o lote. `commit=true` grava uma única vez — e, diferente de
   * `persist()`, propaga o erro em vez de só avisar no console: um lote de
   * importação precisa saber que a gravação falhou (armazenamento cheio,
   * por exemplo) para reverter o que criou, em vez de reportar sucesso com
   * dado que não sobrevive a um F5. `commit=false` descarta a gravação
   * pendente sem tocar no que já estava salvo — o repositório em memória não
   * desfaz as mutações dos Maps (isso é papel da reversão compensatória em
   * `EquipmentImportService`), mas evita gravar um estado que vai ser
   * desfeito no próximo instante.
   */
  async endBatch(commit: boolean): Promise<void> {
    this.batchDepth = Math.max(0, this.batchDepth - 1);
    if (this.batchDepth > 0) return;
    if (!commit) return;
    this.writeToStorage();
  }

  private persist() {
    if (this.batchDepth > 0) return;
    try {
      this.writeToStorage();
    } catch (e) {
      console.warn("Failed to save equipment to localStorage", e);
    }
  }

  private writeToStorage(): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem('inspect_equipments', JSON.stringify(Array.from(this.equipments.entries())));
    localStorage.setItem('inspect_snapshots', JSON.stringify(Array.from(this.snapshots.entries())));
    localStorage.setItem('inspect_equipment_documents', JSON.stringify(Array.from(this.documents.entries())));
  }

  async getEquipment(clientId: ClientId, id: EquipmentId): Promise<Equipment | null> {
    const eq = this.equipments.get(id);
    if (eq && (!clientId || eq.clientId === clientId)) {
      return eq;
    }
    return null;
  }

  async listEquipments(clientId?: ClientId, options?: { projectId?: string | null }): Promise<Equipment[]> {
    let list = Array.from(this.equipments.values());
    if (clientId) list = list.filter(eq => eq.clientId === clientId);
    if (options?.projectId) list = list.filter(eq => eq.projectId === options.projectId);
    return list;
  }

  async saveEquipment(equipment: Equipment): Promise<void> {
    this.equipments.set(equipment.id, equipment);
    this.persist();
  }

  async deleteEquipment(clientId: ClientId, id: EquipmentId): Promise<void> {
    const eq = this.equipments.get(id);
    if (eq && eq.clientId === clientId) {
      this.equipments.delete(id);
      this.persist();
    }
  }

  async saveSnapshot(snapshot: EquipmentSnapshot): Promise<void> {
    this.snapshots.set(snapshot.id, snapshot);
    this.persist();
  }

  async getSnapshot(id: string): Promise<EquipmentSnapshot | null> {
    return this.snapshots.get(id) || null;
  }

  async listSnapshotsByEquipment(clientId: ClientId, equipmentId: EquipmentId): Promise<EquipmentSnapshot[]> {
    return Array.from(this.snapshots.values()).filter(
      s => s.clientId === clientId && s.equipmentId === equipmentId
    );
  }

  async deleteSnapshotsByEquipment(clientId: ClientId, equipmentId: EquipmentId): Promise<void> {
    for (const [id, snapshot] of this.snapshots.entries()) {
      if (snapshot.clientId === clientId && snapshot.equipmentId === equipmentId) {
        this.snapshots.delete(id);
      }
    }
    this.persist();
  }

  async saveDocument(doc: EquipmentDocument): Promise<void> {
    this.documents.set(doc.id, doc);
    this.persist();
  }

  async listDocuments(clientId: ClientId, equipmentId: EquipmentId): Promise<EquipmentDocument[]> {
    return Array.from(this.documents.values()).filter(
      d => d.clientId === clientId && d.equipmentId === equipmentId
    );
  }

  async removeDocument(clientId: ClientId, documentId: string): Promise<void> {
    const doc = this.documents.get(documentId);
    if (doc && doc.clientId === clientId) {
      this.documents.delete(documentId);
      this.persist();
    }
  }
}
